// Per-layer status: last-updated timestamps, loading flags, errors, counts.
// Used by the layer sheet to show "Updated 2m ago" + spinner + error icon.

import { create } from 'zustand';

export type LayerKey =
  | 'tfr'
  | 'sua'
  | 'fires'
  | 'viirs'
  | 'airmet'
  | 'sigmet'
  | 'fireguard'
  | 'nirops'
  // GFA hazards share one fetch + one status row in the LayerSheet,
  // so all 8 GFA layers point at this single 'gfa' status key.
  | 'gfa'
  | 'cwa'
  | 'flightcat'
  | 'pireps'
  | 'nexrad'
  | 'hrrr-smoke'
  | 'sfcanalysis'
  | 'satgeocolor'
  | 'satir'
  | 'dispatch'
  | 'obstacles'
  | 'repeaters'
  | 'traffic'
  | 'classBCD'
  | 'airways'
  | 'airport-da';

interface LayerStatus {
  loading: boolean;
  updatedAt: number | null;
  error: string | null;
  count: number | null;
}

interface State {
  byKey: Record<LayerKey, LayerStatus>;
  setLoading: (k: LayerKey, v: boolean) => void;
  setUpdated: (k: LayerKey, ts: number, count: number) => void;
  setError: (k: LayerKey, msg: string) => void;
}

const empty = (): LayerStatus => ({ loading: false, updatedAt: null, error: null, count: null });

export const useLayerStatusStore = create<State>((set) => ({
  byKey: {
    tfr: empty(),
    sua: empty(),
    fires: empty(),
    viirs: empty(),
    airmet: empty(),
    sigmet: empty(),
    fireguard: empty(),
    nirops: empty(),
    gfa: empty(),
    cwa: empty(),
    flightcat: empty(),
    pireps: empty(),
    nexrad: empty(),
    'hrrr-smoke': empty(),
    sfcanalysis: empty(),
    satgeocolor: empty(),
    satir: empty(),
    dispatch: empty(),
    repeaters: empty(),
    obstacles: empty(),
    traffic: empty(),
    classBCD: empty(),
    airways: empty(),
    'airport-da': empty(),
  },
  setLoading: (k, v) => set((s) => ({
    byKey: { ...s.byKey, [k]: { ...s.byKey[k], loading: v } },
  })),
  setUpdated: (k, ts, count) => set((s) => ({
    byKey: { ...s.byKey, [k]: { ...s.byKey[k], updatedAt: ts, error: null, count } },
  })),
  setError: (k, msg) => set((s) => ({
    byKey: { ...s.byKey, [k]: { ...s.byKey[k], error: msg } },
  })),
}));

/** Format a timestamp as "just now / 2m ago / 1h 5m ago / never". */
export function formatRelative(ts: number | null): string {
  if (!ts) return 'never';
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  const m = min - h * 60;
  return `${h}h ${m}m ago`;
}
